import Login from "./login";
import { useEffect } from "react";
import { useSpinnerContext } from '@contexts';
import { useNavigate, useLocation } from "react-router-dom";
import { useFormSubmission, useFeatureFlag } from "@hooks";
import { FeatureFlag } from "@root/enums";
import { sunboundPayProxy } from '@proxies';
import { authStore, redirectWithAuthTransfer, startOAuthLogin, takeOAuthLoginResult, type OAuthError, type OAuthProvider } from '@stores';
import { helpers, SSO_PROVIDERS, getSsoProviderName } from '@shared';
import { Button } from "@common";
import { PostHogEvents, capturePosthogEvent } from '@analytics';
import { TriangleAlert } from "lucide-react";

interface LoginValues {
    email: string;
    password: string;
}

const EMAIL_LOGIN_ACCOUNT_MISMATCH_MESSAGE = `The information above doesn't match an account. Please try again.`;

const getOAuthErrorMessage = (error: OAuthError, providerName: string): string =>
    error === `unknown_payor` || error === `payor_mismatch`
        ? `This ${providerName} account isn't associated with a payor account. Please try another account.`
        : `We couldn't sign you in with ${providerName}. Please try again.`;

const UserLogin = () => {

    const { setSpinnerShow } = useSpinnerContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { value: isSsoEnabled } = useFeatureFlag(FeatureFlag.SSO_ENABLED);
    const from = location.state?.from?.pathname || `/`;
    const sessionExpired = location.state?.unauthorized === true && (() => {
        const expiresAt = authStore.tokenExpiresAt;
        return !!expiresAt && new Date(expiresAt) < new Date();
    })();

    const {
        setFormState: setEmailLoginError,
        hasFormError: hasEmailLoginError,
        formError: emailLoginError
    } = useFormSubmission();
    const {
        setFormState: setSsoLoginError,
        clearFormState: clearSsoLoginError,
        hasFormError: hasSsoLoginError,
        formError: ssoLoginError
    } = useFormSubmission();

    useEffect(() => {
        const oauthLoginResult = takeOAuthLoginResult();
        if (!oauthLoginResult) return;

        if (oauthLoginResult.status === `error`) {
            setSsoLoginError(getOAuthErrorMessage(oauthLoginResult.error, getSsoProviderName(oauthLoginResult.provider)));
            return;
        }

        navigate(oauthLoginResult.redirectPath ?? from, { replace: true });
    }, [from, navigate, setSsoLoginError]);

    const handleSsoLogin = (provider: OAuthProvider) => {
        clearSsoLoginError();
        startOAuthLogin(provider, from);
    };

    const handleSubmit = async (values: LoginValues) => {

        setSpinnerShow(true);
        try {
            const response = await sunboundPayProxy.login(values.email, values.password);
            capturePosthogEvent(PostHogEvents.USER_LOGGED_IN, { loginMethod: `email` });
            if (response?.communityShortName) {
                const targetHost = helpers.getCommunitySubdomain(response.communityShortName);
                if (window.location.host !== targetHost) {
                    await redirectWithAuthTransfer(targetHost, from);
                    return;
                }
            }
            navigate(from, { replace: true });
        } catch (error) {
            console.error(error);
            setEmailLoginError(EMAIL_LOGIN_ACCOUNT_MISMATCH_MESSAGE);
        }
        setSpinnerShow(false);
    };

    return (
        <div className="flex flex-col w-full gap-6">
            {sessionExpired && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                        <TriangleAlert className="w-5 h-5 text-red-700" />
                        <h4 className="font-medium text-base leading-6 text-red-700 tracking-[0.16px]">Your session has expired</h4>
                    </div>
                    <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px]">Please log in again to continue.</p>
                </div>
            )}
            <Login submit={handleSubmit}>
                <div className="border-b border-neutral-100 pb-6 mb-6 md:mb-8">
                    <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">Welcome</h1>
                </div>
            </Login>
            {
                hasEmailLoginError &&
                <p className="text-red-600 text-sm mt-4 text-center">{emailLoginError}</p>
            }
            {isSsoEnabled && (
                <>
                    <div className="flex items-center gap-4">
                        <div className="flex-1 border-t border-neutral-200" />
                        <span className="text-sm text-neutral-500">or</span>
                        <div className="flex-1 border-t border-neutral-200" />
                    </div>
                    {SSO_PROVIDERS.map(({ provider, name, logo: Logo }) => (
                        <Button
                            key={provider}
                            variant="secondary"
                            size="lg"
                            className="w-full"
                            startIcon={<Logo className="mr-2" />}
                            onClick={() => handleSsoLogin(provider)}
                        >
                            Continue with {name}
                        </Button>
                    ))}
                    {
                        hasSsoLoginError &&
                        <p className="text-red-600 text-sm mt-4 text-center">{ssoLoginError}</p>
                    }
                </>
            )}
        </div>
    );

};


export default UserLogin;
